import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Lightbulb, Rocket, Users, Cpu, Eye, Calendar, Briefcase, Heart } from 'lucide-react';

const Icon = ({ name, size = 24, className = "" }) => {
  const icons = {
    Clock, Lightbulb, Rocket, Users, Cpu, Eye, Calendar, Briefcase, Heart
  };
  const IconComponent = icons[name];
  return IconComponent ? <IconComponent size={size} className={className} /> : null;
};

const OriginStory = () => {
  const [activeYear, setActiveYear] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let observer;
    const element = document.getElementById('origin-story');
    
    if (element) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        },
        { threshold: 0.3 }
      );

      observer.observe(element);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const timelineData = [
    {
      year: "2024",
      month: "NOV",
      title: "The Spark",
      description: `Founded by three passionate developers who believed that software should be more than functional—it should be transformative. Started in a small garage with big dreams.`,
      icon: "Lightbulb",
      color: "from-accent/20 to-accent/40"
    },
    {
      year: "2025",
      month: "MAR",
      title: "First Breakthrough",
      description: `Delivered our first major project—a 3D interactive platform for a tech startup that increased their user engagement by 300%. This success defined our approach.`,
      icon: "Rocket",
      color: "from-success/20 to-success/40"
    },
    {
      year: "2025",
      month: "JUN",
      title: "Team Expansion",
      description: `Grew from 3 to 12 talented individuals, each bringing unique expertise in 3D development, UX design, and cutting-edge technologies. Our culture began to take shape.`,
      icon: "Users",
      color: "from-warning/20 to-warning/40"
    },
    {
      year: "2025",
      month: "SEP",
      title: "Innovation Lab",
      description: `Established our R&D division focusing on emerging technologies like WebGL, AR/VR, and AI integration. Started contributing to open-source projects.`,
      icon: "Cpu",
      color: "from-purple-500/20 to-purple-500/40"
    },
    {
      year: "2025",
      month: "DEC",
      title: "Future Vision",
      description: `Today, we're 15+ strong, working with Fortune 500 companies and innovative startups. We continue to lift the shutter on digital possibilities worldwide.`,
      icon: "Eye",
      color: "from-accent/20 to-accent/40"
    }
  ];

  return (
    // <section id="origin-story" className="py-20 bg-background relative overflow-hidden">
    <section id="origin-story" className="py-12 md:py-20 bg-background relative overflow-hidden">

      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-64 h-64 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-success/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
            <Icon name="Clock" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Our Journey</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Origin Story
          </h2>
          <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
            From a garage startup to a global innovation powerhouse—discover how we've been lifting the shutter on digital possibilities since day one.
          </p>
        </motion.div>

        {/* Timeline - Mobile: Year Left, Content Right */}
        <div className="relative">
          {/* Vertical Line - Hidden on Mobile */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border transform -translate-x-1/2"></div>

          {/* Timeline Items */}
          <div className="space-y-8 md:space-y-16">
            {timelineData?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                animate={isVisible ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="relative"
                onMouseEnter={() => setActiveYear(index)}
              >
                {/* Mobile Layout: Year Left, Content Right */}
                <div className="flex items-start gap-4 md:hidden">
                  {/* Year on Left - Mobile */}
                  <div className="flex-shrink-0 w-20">
                    <motion.div
                      animate={activeYear === index ? { scale: 1.1 } : { scale: 1 }}
                      className="text-3xl font-bold text-accent/40"
                    >
                      {item?.year}
                    </motion.div>
                    <motion.div
                      whileHover={{ rotateZ: 180, scale: 1.2 }}
                      transition={{ duration: 0.5 }}
                      className={`w-12 h-12 rounded-full border-4 flex items-center justify-center font-bold text-xs mt-2 ${
                        activeYear === index
                          ? 'bg-accent border-accent text-accent-foreground'
                          : 'bg-card border-border text-foreground'
                      }`}
                    >
                      {item?.month}
                    </motion.div>
                  </div>

                  {/* Content on Right - Mobile */}
                  <div className="flex-1">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`bg-card border border-border rounded-xl p-4 shadow-lg ${
                        activeYear === index ? 'shadow-accent/20 border-accent/30' : ''
                      }`}
                    >
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${item?.color} mb-3`}>
                        <Icon name={item?.icon} size={20} className="text-foreground" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2">{item?.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item?.description}</p>
                    </motion.div>
                  </div>
                </div>

                {/* Desktop Layout: Alternating */}
                <div className={`hidden md:flex items-center ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}>
                  {/* Content Card */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                    <motion.div
                      whileHover={{ scale: 1.05, rotateY: 5 }}
                      className={`bg-card border border-border rounded-2xl p-8 shadow-3d transform-3d perspective-1000 cursor-pointer ${
                        activeYear === index ? 'shadow-accent border-accent/30' : ''
                      }`}
                    >
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${item?.color} mb-4`}>
                        <Icon name={item?.icon} size={24} className="text-foreground" />
                      </div>
                      <h3 className="text-2xl font-bold text-foreground mb-2">{item?.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{item?.description}</p>
                    </motion.div>
                  </div>

                  {/* Timeline Node - Desktop */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                    <motion.div
                      whileHover={{ rotateZ: 180, scale: 1.2 }}
                      transition={{ duration: 0.5 }}
                      className={`w-16 h-16 rounded-full border-4 flex items-center justify-center font-bold text-sm shadow-3d ${
                        activeYear === index
                          ? 'bg-accent border-accent text-accent-foreground'
                          : 'bg-card border-border text-foreground'
                      }`}
                    >
                      {item?.month}
                    </motion.div>
                  </div>

                  {/* Year Label - Desktop */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pl-8 text-left' : 'pr-8 text-right'}`}>
                    <motion.div
                      animate={activeYear === index ? { scale: 1.1 } : { scale: 1 }}
                      className="text-6xl font-bold text-accent/20"
                    >
                      {item?.year}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-12 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8"
        > */}
         {/* removing the gap */}
         <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
         transition={{ duration: 0.8, delay: 1.2 }}
         className="mt-8 md:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8"
          >
          {[
            { label: "Years of Innovation", value: "2+", icon: "Calendar" },
            { label: "Team Members", value: "15+", icon: "Users" },
            { label: "Projects Delivered", value: "50+", icon: "Briefcase" },
            { label: "Happy Clients", value: "40+", icon: "Heart" }
          ]?.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="text-center p-4 md:p-6 bg-card/50 border border-border rounded-xl backdrop-blur-sm"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-xl mb-3 md:mb-4">
                <Icon name={stat?.icon} size={20} className="text-accent md:w-6 md:h-6" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">{stat?.value}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{stat?.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default OriginStory;